import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors 
} from '@dnd-kit/core';
import { 
  arrayMove, 
  SortableContext, 
  sortableKeyboardCoordinates, 
  rectSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Loader2, Plus, Pencil, Trash2, Search, Package, Leaf, Copy, Upload, Link, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const API_URL = window.location.origin;

// Sortable Product Card Component
const SortableProductCard = ({ product, onEdit, onDuplicate, onDelete, getCategoryName, getMealTypeBadgeClass }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style}
      className={`border-slate-200/60 shadow-sm overflow-hidden ${isDragging ? 'shadow-lg ring-2 ring-green-500' : ''}`}
      data-testid={`product-card-${product.id}`}
    >
      <div className="aspect-video relative overflow-hidden bg-slate-100">
        {/* Drag Handle */}
        <div 
          {...attributes} 
          {...listeners}
          className="absolute top-2 left-2 z-10 bg-white/90 backdrop-blur-sm rounded-lg p-1.5 cursor-grab active:cursor-grabbing shadow-sm hover:bg-white transition-colors"
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4 text-slate-500" />
        </div>
        
        {product.image_url ? (
          <img 
            src={product.image_url} 
            alt={product.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <Package className="w-12 h-12" />
          </div>
        )}
        {!product.is_active && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Badge className="bg-red-500 text-white">Inactive</Badge>
          </div>
        )}
        {product.is_vegetarian && (
          <Badge className="absolute top-2 right-2 bg-green-100 text-green-700">
            <Leaf className="w-3 h-3 mr-1" />
            V
          </Badge>
        )}
      </div>
      <CardContent className="p-4">
        <h3 className="font-medium text-slate-900 mb-1 line-clamp-2">{product.name}</h3>
        <p className="text-xs text-slate-500 mb-2 line-clamp-1">
          {getCategoryName(product.category_id).split(':')[0]}
        </p>
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-1 flex-wrap">
            <Badge className={`${getMealTypeBadgeClass(product.meal_type_tag)} text-xs`}>
              {product.meal_type_tag}
            </Badge>
            {product.menu_weeks?.length > 0 && product.menu_weeks.map(w => (
              <Badge key={w} className="bg-amber-100 text-amber-700 text-xs">W{w}</Badge>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDuplicate(product.id)}
              className="h-8 w-8 text-slate-600 hover:text-slate-900"
              title="Duplicate"
              data-testid={`duplicate-product-${product.id}`}
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(product)}
              className="h-8 w-8"
              data-testid={`edit-product-${product.id}`}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(product.id)}
              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
              data-testid={`delete-product-${product.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    image_url: '',
    meal_type_tag: 'Chilled Meal',
    is_vegetarian: false,
    is_active: true,
    menu_weeks: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [imageTab, setImageTab] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const fileInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [prodRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/products/all`, { withCredentials: true }),
        axios.get(`${API_URL}/api/categories/all`, { withCredentials: true })
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
    } catch (e) {
      // Error handled silently
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products
    .filter(p => {
      const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = filterCategory === 'all' || p.category_id === filterCategory;
      const matchesStatus = filterStatus === 'all' || (filterStatus === 'active' ? p.is_active !== false : p.is_active === false);
      return matchesSearch && matchesCategory && matchesStatus;
    })
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'Unknown';
  };

  const getMealTypeBadgeClass = (mealType) => {
    if (mealType?.includes('Chilled')) return 'bg-blue-100 text-blue-700';
    if (mealType?.includes('Hybrid')) return 'bg-purple-100 text-purple-700';
    if (mealType?.includes('Heat')) return 'bg-orange-100 text-orange-700';
    return 'bg-slate-100 text-slate-700';
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = filteredProducts.findIndex(p => p.id === active.id);
      const newIndex = filteredProducts.findIndex(p => p.id === over.id);

      const newOrder = arrayMove(filteredProducts, oldIndex, newIndex);
      
      // Update local state immediately for responsive UI
      const updatedProducts = products.map(p => {
        const newIdx = newOrder.findIndex(np => np.id === p.id);
        if (newIdx !== -1) {
          return { ...p, sort_order: newIdx };
        }
        return p;
      });
      setProducts(updatedProducts);

      // Save to backend
      setSavingOrder(true);
      try {
        const productOrders = newOrder.map((p, idx) => ({
          id: p.id,
          sort_order: idx
        }));

        await axios.put(`${API_URL}/api/products/reorder`, 
          { products: productOrders }, 
          { withCredentials: true }
        );
        toast.success('Order saved');
      } catch (e) {
        // Error handled silently
        toast.error('Failed to save order');
        fetchData(); // Revert on error
      } finally {
        setSavingOrder(false);
      }
    }
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description || '',
        category_id: product.category_id,
        image_url: product.image_url || '',
        meal_type_tag: product.meal_type_tag,
        is_vegetarian: product.is_vegetarian,
        is_active: product.is_active,
        menu_weeks: product.menu_weeks || []
      });
      setImageTab(product.image_url ? 'url' : 'upload');
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        category_id: categories[0]?.id || '',
        image_url: '',
        meal_type_tag: 'Chilled Meal',
        is_vegetarian: false,
        is_active: true,
        menu_weeks: []
      });
      setImageTab('url');
    }
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, GIF, WebP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const { data } = await axios.post(`${API_URL}/api/upload/image`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = `${API_URL}${data.url}`;
      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploaded successfully');
    } catch (e) {
      // Error handled silently
      toast.error(e.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        sort_order: editingProduct ? editingProduct.sort_order : filteredProducts.length
      };

      if (editingProduct) {
        await axios.put(`${API_URL}/api/products/${editingProduct.id}`, submitData, { withCredentials: true });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API_URL}/api/products`, submitData, { withCredentials: true });
        toast.success('Product created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      // Error handled silently
      toast.error(e.response?.data?.detail || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDuplicate = async (productId) => {
    try {
      await axios.post(`${API_URL}/api/products/${productId}/duplicate`, {}, { withCredentials: true });
      toast.success('Product duplicated successfully');
      fetchData();
    } catch (e) {
      // Error handled silently
      toast.error('Failed to duplicate product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/products/${productId}`, { withCredentials: true });
      toast.success('Product deleted successfully');
      fetchData();
    } catch (e) {
      // Error handled silently
      toast.error('Failed to delete product');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Products
            </h1>
            <p className="text-slate-500 mt-1">Manage menu items • Drag to reorder</p>
          </div>
          <div className="flex items-center gap-3">
            {savingOrder && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </div>
            )}
            <Button 
              onClick={() => handleOpenDialog()}
              className="rounded-full bg-green-600 hover:bg-green-700"
              data-testid="add-product-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl"
              data-testid="search-products-input"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-[250px] rounded-xl" data-testid="filter-category-select">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name.split(':')[0]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[160px] rounded-xl" data-testid="filter-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
              <SelectItem value="all">All Products</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Drag & Drop Info */}
        {filteredProducts.length > 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center gap-2">
            <GripVertical className="w-4 h-4 text-slate-400" />
            <p className="text-sm text-slate-600">
              Drag products by the grip handle to reorder them. Changes are saved automatically.
            </p>
          </div>
        )}

        {/* Products Grid with Drag & Drop */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No products found</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={filteredProducts.map(p => p.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map((product) => (
                  <SortableProductCard
                    key={product.id}
                    product={product}
                    onEdit={handleOpenDialog}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                    getCategoryName={getCategoryName}
                    getMealTypeBadgeClass={getMealTypeBadgeClass}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Add/Edit Product Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Mixed Cheese Sandwich (M)"
                  className="rounded-xl"
                  data-testid="product-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Brief description of this product"
                  data-testid="product-description-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category *</Label>
                <Select 
                  value={formData.category_id} 
                  onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="product-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Image Upload Section */}
              <div className="space-y-2">
                <Label>Product Image</Label>
                <Tabs value={imageTab} onValueChange={setImageTab}>
                  <TabsList className="bg-slate-100 p-1 rounded-lg w-full">
                    <TabsTrigger value="url" className="flex-1 rounded-md data-[state=active]:bg-white">
                      <Link className="w-4 h-4 mr-2" />
                      URL
                    </TabsTrigger>
                    <TabsTrigger value="upload" className="flex-1 rounded-md data-[state=active]:bg-white">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="url" className="mt-3">
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      className="rounded-xl"
                      data-testid="product-image-url-input"
                    />
                  </TabsContent>
                  
                  <TabsContent value="upload" className="mt-3">
                    <div className="space-y-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="image-upload"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-full rounded-xl"
                      >
                        {uploading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Choose Image
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-slate-500">
                        Allowed: JPEG, PNG, GIF, WebP. Max 5MB.
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
                
                {formData.image_url && (
                  <div className="mt-3 aspect-video max-w-[200px] rounded-lg overflow-hidden bg-slate-100 border">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="meal_type_tag">Meal Type *</Label>
                <Select 
                  value={formData.meal_type_tag} 
                  onValueChange={(value) => setFormData({ ...formData, meal_type_tag: value })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="product-meal-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Chilled Meal">Chilled Meal</SelectItem>
                    <SelectItem value="Hybrid Meal">Hybrid Meal</SelectItem>
                    <SelectItem value="Heat to Eat Meal">Heat to Eat Meal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="is_vegetarian">Vegetarian</Label>
                <Switch
                  id="is_vegetarian"
                  checked={formData.is_vegetarian}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_vegetarian: checked })}
                  data-testid="product-vegetarian-switch"
                />
              </div>

              {/* Menu Weeks - Only for Rotating categories */}
              {(() => {
                const selectedCat = categories.find(c => c.id === formData.category_id);
                if (selectedCat?.rotation_type === 'Rotating') {
                  return (
                    <div className="space-y-2">
                      <Label>Menu Weeks (Rotating)</Label>
                      <p className="text-xs text-slate-500">Select which weeks this product appears in the 3-week cycle</p>
                      <div className="flex gap-2">
                        {[1, 2, 3].map(week => {
                          const isSelected = formData.menu_weeks.includes(week);
                          return (
                            <button key={week} type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  menu_weeks: isSelected 
                                    ? prev.menu_weeks.filter(w => w !== week)
                                    : [...prev.menu_weeks, week]
                                }));
                              }}
                              className={`flex-1 py-2 rounded-xl border-2 text-sm font-medium transition-all ${
                                isSelected ? 'border-green-500 bg-green-50 text-green-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'
                              }`}
                              data-testid={`menu-week-${week}-toggle`}
                            >
                              Week {week}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              <div className="flex items-center justify-between">
                <Label htmlFor="is_active">Active</Label>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="product-active-switch"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="rounded-full bg-green-600 hover:bg-green-700"
                  data-testid="save-product-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    editingProduct ? 'Update Product' : 'Create Product'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
